import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  CreateDeductionParams,
  CreateDeductionResponse,
  CreatePaymentParams,
  CreatePaymentRateParams,
  CreatePaymentRateResponse,
  CreatePaymentResponse,
  GetDeductionByIdParams,
  GetDeductionByIdResponse,
  GetDeductionListParams,
  GetDeductionListResponse,
  GetPaymentByIdParams,
  GetPaymentByIdResponse,
  GetPaymentListParams,
  GetPaymentListResponse,
  GetPaymentRateByIdParams,
  GetPaymentRateByIdResponse,
  GetPaymentRateListParams,
  GetPaymentRateListResponse,
  GetVATCodeListParams,
  GetVATCodeListResponse,
  UpdateDeductionParams,
  UpdateDeductionResponse,
  UpdatePaymentParams,
  UpdatePaymentRateParams,
  UpdatePaymentRateResponse,
  UpdatePaymentResponse,
} from '../types';

export const getPaymentList = createAsyncThunk<
  GetPaymentListResponse,
  GetPaymentListParams
>(
  'paymentSetting/getPaymentList',
  async (params: GetPaymentListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.paymentsSetting.payments, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updatePaymentById = createAsyncThunk<
  UpdatePaymentResponse,
  UpdatePaymentParams
>(
  'paymentSetting/updatePaymentById',
  async (params: UpdatePaymentParams, { rejectWithValue }) => {
    try {
      const { id, payload } = params;
      const response = await jwtAxios.put(
        `${endPoints.paymentsSetting.payments}/${id}`,
        payload,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPaymentById = createAsyncThunk<
  GetPaymentByIdResponse,
  GetPaymentByIdParams
>(
  'paymentSetting/getPaymentById',
  async (params: GetPaymentByIdParams, { rejectWithValue }) => {
    try {
      const { id } = params;
      const response = await jwtAxios.get(
        `${endPoints.paymentsSetting.payments}/${id}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const createPayment = createAsyncThunk<
  CreatePaymentResponse,
  CreatePaymentParams
>(
  'paymentSetting/createPayment',
  async (params: CreatePaymentParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.paymentsSetting.payments,
        params,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getDeductionList = createAsyncThunk<
  GetDeductionListResponse,
  GetDeductionListParams
>(
  'paymentSetting/getDeductionList',
  async (params: GetDeductionListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.paymentsSetting.deductions, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateDeductionById = createAsyncThunk<
  UpdateDeductionResponse,
  UpdateDeductionParams
>(
  'paymentSetting/updateDeductionById',
  async (params: UpdateDeductionParams, { rejectWithValue }) => {
    try {
      const { id, payload } = params;
      const response = await jwtAxios.put(
        `${endPoints.paymentsSetting.deductions}/${id}`,
        payload,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getDeductionById = createAsyncThunk<
  GetDeductionByIdResponse,
  GetDeductionByIdParams
>(
  'paymentSetting/getDeductionById',
  async (params: GetDeductionByIdParams, { rejectWithValue }) => {
    try {
      const { id } = params;
      const response = await jwtAxios.get(
        `${endPoints.paymentsSetting.deductions}/${id}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const createDeduction = createAsyncThunk<
  CreateDeductionResponse,
  CreateDeductionParams
>(
  'paymentSetting/createDeduction',
  async (params: CreateDeductionParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.paymentsSetting.deductions,
        params,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPaymentRateList = createAsyncThunk<
  GetPaymentRateListResponse,
  GetPaymentRateListParams
>(
  'paymentSetting/getPaymentRateList',
  async (params: GetPaymentRateListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.paymentRate.main, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const createPaymentRate = createAsyncThunk<
  CreatePaymentRateResponse,
  CreatePaymentRateParams
>(
  'paymentSetting/createPaymentRate',
  async (params: CreatePaymentRateParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.paymentRate.main,
        params,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updatePaymentRateById = createAsyncThunk<
  UpdatePaymentRateResponse,
  UpdatePaymentRateParams
>(
  'paymentSetting/updatePaymentRateById',
  async (params: UpdatePaymentRateParams, { rejectWithValue }) => {
    try {
      const { id, payload } = params;
      const response = await jwtAxios.put(
        `${endPoints.paymentRate.main}/${id}`,
        payload,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPaymentRateById = createAsyncThunk<
  GetPaymentRateByIdResponse,
  GetPaymentRateByIdParams
>(
  'paymentSetting/getPaymentRateById',
  async (params: GetPaymentRateByIdParams, { rejectWithValue }) => {
    try {
      const { id } = params;
      const response = await jwtAxios.get(
        `${endPoints.paymentRate.main}/${id}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getVATCodeList = createAsyncThunk<
  GetVATCodeListResponse,
  GetVATCodeListParams
>(
  'paymentSetting/getVATCodeList',
  async (params: GetVATCodeListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.paymentRate.vatCode, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const paymentSettingActs = {
  getPaymentList,
  updatePaymentById,
  getPaymentById,
  createPayment,
  getDeductionList,
  updateDeductionById,
  getDeductionById,
  createDeduction,
  getPaymentRateList,
  createPaymentRate,
  updatePaymentRateById,
  getPaymentRateById,
  getVATCodeList,
};
